import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { api } from "../../utils/api";

const formatDateTime = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString();
};

const formatAmount = (value) =>
  value == null || Number.isNaN(Number(value)) ? "Store price" : `$${Number(value).toFixed(2)}`;

const messageFrom = (response, fallback) => response?.data?.message || fallback;

/**
 * Coffee Pass redemptions have no vehicle photo to verify. Admins either let each
 * per-coffee Xero bill queue automatically, or publish them here in bulk.
 */
const CoffeePassBills = () => {
  const [autoQueue, setAutoQueue] = useState(false);
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [savingToggle, setSavingToggle] = useState(false);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loadingRows, setLoadingRows] = useState(true);
  const [selected, setSelected] = useState(() => new Set());
  const [publishing, setPublishing] = useState(false);
  const [banner, setBanner] = useState(null);

  const loadSettings = useCallback(async () => {
    try {
      const response = await api("v1/settings/coffee-pass", "GET");
      if (response?.data?.success) {
        setAutoQueue(response.data.data.autoQueueCafeXeroBills === true);
        setSettingsLoaded(true);
      } else {
        setBanner({ severity: "error", text: messageFrom(response, "Could not load the Coffee Pass setting.") });
      }
    } catch (error) {
      setBanner({ severity: "error", text: "Could not load the Coffee Pass setting." });
    }
  }, []);

  const loadRows = useCallback(async () => {
    setLoadingRows(true);
    try {
      const response = await api("v1/redemption/cafe-unbilled", "GET");
      if (response?.data?.success) {
        setRows(response.data.data.redemptions || []);
        setTotal(response.data.data.total || 0);
        setSelected(new Set());
      } else {
        setBanner({ severity: "error", text: messageFrom(response, "Could not load Coffee Pass redemptions.") });
      }
    } catch (error) {
      setBanner({ severity: "error", text: "Could not load Coffee Pass redemptions." });
    } finally {
      setLoadingRows(false);
    }
  }, []);

  useEffect(() => {
    loadSettings();
    loadRows();
  }, [loadSettings, loadRows]);

  const toggleAutoQueue = async (event) => {
    const next = event.target.checked;
    setSavingToggle(true);
    try {
      const response = await api("v1/settings/coffee-pass", "PATCH", { autoQueueCafeXeroBills: next });
      if (response?.data?.success) {
        setAutoQueue(response.data.data.autoQueueCafeXeroBills === true);
        setBanner({ severity: "success", text: response.data.message });
      } else {
        setBanner({ severity: "error", text: messageFrom(response, "Could not save the setting.") });
      }
    } catch (error) {
      setBanner({ severity: "error", text: "Could not save the setting." });
    } finally {
      setSavingToggle(false);
    }
  };

  const publish = async (body) => {
    setPublishing(true);
    try {
      const response = await api("v1/redemption/cafe-publish-bills", "POST", body);
      setBanner({
        severity: response?.data?.success ? "success" : "error",
        text: messageFrom(response, "Could not publish the bills."),
      });
      if (response?.data?.success) await loadRows();
    } catch (error) {
      setBanner({ severity: "error", text: "Could not publish the bills." });
    } finally {
      setPublishing(false);
    }
  };

  const allSelected = rows.length > 0 && selected.size === rows.length;
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(rows.map((r) => r._id)));
  const toggleOne = (id) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const missingXeroContact = useMemo(() => rows.some((r) => !r.merchantHasXeroContact), [rows]);

  return (
    <Box className="w-full flex flex-column gap-4 p-4">
      <Typography variant="h5">Coffee Pass bills</Typography>
      <Typography variant="body2" color="text.secondary">
        Coffee Pass redemptions have no vehicle photo to check. Each coffee is billed to the cafe through Xero at
        the store pricing amount, less the admin fee.
      </Typography>

      {banner && (
        <Alert severity={banner.severity} onClose={() => setBanner(null)}>
          {banner.text}
        </Alert>
      )}

      <Paper className="p-4">
        <FormControlLabel
          control={
            <Switch
              checked={autoQueue}
              onChange={toggleAutoQueue}
              disabled={!settingsLoaded || savingToggle}
              inputProps={{ "aria-label": "Automatically queue Coffee Pass Xero bills" }}
            />
          }
          label="Automatically queue a Xero bill for every coffee"
        />
        <Typography variant="body2" color="text.secondary">
          {autoQueue
            ? "On: each new Coffee Pass redemption is queued for Xero straight away. Redemptions already listed below still need publishing."
            : "Off: new Coffee Pass redemptions wait in the table below until you publish them."}
        </Typography>
      </Paper>

      <Paper className="p-4 flex flex-column gap-3">
        <Box className="flex flex-wrap items-center justify-between gap-2">
          <Typography variant="subtitle1">
            Waiting to publish: {total}
            {total > rows.length ? ` (showing ${rows.length})` : ""}
          </Typography>
          <Box className="flex gap-2">
            <Button variant="outlined" onClick={loadRows} disabled={loadingRows || publishing}>
              Refresh
            </Button>
            <Button
              variant="contained"
              onClick={() => publish({ redemptionIds: Array.from(selected) })}
              disabled={selected.size === 0 || publishing}
            >
              Publish selected ({selected.size})
            </Button>
            <Button
              variant="contained"
              color="secondary"
              onClick={() => {
                if (window.confirm(`Queue Xero bills for all ${total} unbilled Coffee Pass redemptions?`)) {
                  publish({ all: true });
                }
              }}
              disabled={total === 0 || publishing}
            >
              Publish all
            </Button>
          </Box>
        </Box>

        {missingXeroContact && (
          <Alert severity="warning">
            Some cafes have no linked Xero contact. Their bills will fail on the billing run until the contact is
            linked on the merchant account.
          </Alert>
        )}

        {loadingRows ? (
          <Box className="flex justify-center p-6">
            <CircularProgress />
          </Box>
        ) : rows.length === 0 ? (
          <Typography color="text.secondary">No Coffee Pass redemptions are waiting to be billed.</Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox checked={allSelected} indeterminate={selected.size > 0 && !allSelected} onChange={toggleAll} />
                  </TableCell>
                  <TableCell>Redeemed</TableCell>
                  <TableCell>Member</TableCell>
                  <TableCell>Cafe</TableCell>
                  <TableCell>Store</TableCell>
                  <TableCell>Product</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell>Redemption</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row._id} hover selected={selected.has(row._id)}>
                    <TableCell padding="checkbox">
                      <Checkbox checked={selected.has(row._id)} onChange={() => toggleOne(row._id)} />
                    </TableCell>
                    <TableCell>{formatDateTime(row.redemptionDateUTC || row.createdOnUTC)}</TableCell>
                    <TableCell>{row.memberName || row.memberEmail || "—"}</TableCell>
                    <TableCell>{row.merchantName || "—"}</TableCell>
                    <TableCell>{row.storeName || "—"}</TableCell>
                    <TableCell>{row.productName || "—"}</TableCell>
                    <TableCell align="right">{formatAmount(row.billingUnitAmount)}</TableCell>
                    <TableCell>
                      <a href={`/redemption/${row._id}`} className="text-blue-600 underline">
                        {String(row._id).slice(-6)}
                      </a>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Box>
  );
};

export default CoffeePassBills;
